/**
 * AccessLabDetailModal.tsx
 *
 * Access Labs result view (plan 2026-10-16-001, U14), cloned from
 * VibrantLabDetailModal: the lab's report, the values with their ranges and
 * flags, and the result's details.
 *
 * Differences from Vibrant:
 *  - A flagged value says High, Low or Abnormal in words; colour is never the
 *    only sign.
 *  - Access sends a specimen's result more than once (preliminary, final,
 *    corrected). The newest is the one lists show; the version picker opens
 *    any earlier one, which says it is earlier and links back to the newest.
 *  - Print opens a report built from the values of the version being viewed
 *    (AccessLabReportPrint). The lab's PDF, when there is one, is also the
 *    viewed version's.
 *  - A result held for staff (no patient) can be assigned from here, as the
 *    Lab Results list's own detail does (LabMatchDialog), or deleted by a
 *    user allowed to, with every version of it.
 *
 * The content is factored into AccessLabDetailBody so it can render inline in
 * the patient-detail right sidebar (LabDetailPanel) and inside the modal shell.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Download, FileText, ChevronDown, ChevronRight, Printer, Trash2, TriangleAlert, UserPlus } from 'lucide-react';
import {
  deleteUnmatchedLabResults,
  getAccessLabResultDetail,
  downloadLabResultFile,
  type AccessLabResultDetail,
} from '@/services/lab-orders.service';
import { Badge, Button, Spinner } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import { LabMatchDialog } from '@/pages/lab-orders/LabMatchDialog';
import { canDeleteLabResult } from '@/pages/lab-orders/labOrdersFilters';
import { VisualRangeBar, FLAG_STYLES } from './VisualRangeBar';
import { AccessResultStatusBadge } from './AccessResultStatusBadge';
import { printAccessLabReport } from './AccessLabReportPrint';
import {
  accessFlagText,
  accessReceivedAt,
  accessVersionLabel,
  formatAccessBirthDate,
  formatAccessDateTime,
  sectionsWithValues,
} from './accessLabFormat';

const LOAD_FAILED = "This result couldn't be loaded. Press Retry to try again, and contact support if it keeps happening.";
const NO_VALUES_PDF = "The values for this result could not be shown. Open the lab's report instead.";
const NO_VALUES = 'No values came with this result. Contact support if you expected some.';
const PDF_UNAVAILABLE = "The lab's report for this result can't be opened. Contact support if you need a copy.";
const PDF_OPEN_FAILED = "The lab's report couldn't be opened. Try again, and contact support if it keeps happening.";
const PRINT_BLOCKED = 'Your browser blocked the print window. Allow pop-ups for this site, then press Print again.';

/** Save a downloaded file under its own name, as VibrantLabDetailBody does. */
async function saveFile(url: string, fallbackName: string): Promise<boolean> {
  const result = await downloadLabResultFile(url);
  if (!result) return false;
  const objectUrl = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = result.filename || fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
  return true;
}

interface AccessLabDetailBodyProps {
  /** labAccessResultID: the version to open first (normally the current one). */
  id: number;
  /** Called after a held result is assigned to a patient. */
  onAssigned?: () => void;
  /** Called after a held result is deleted. */
  onDeleted?: () => void;
}

/**
 * Access result content. Self-contained: fetches the version being viewed.
 * Renders as a full-height flex column so the parent (modal card or sidebar
 * panel) just needs to give it a height.
 */
export function AccessLabDetailBody({ id, onAssigned, onDeleted }: AccessLabDetailBodyProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [viewingId, setViewingId] = useState(id);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['tests', 'documents', 'details']));
  const [actionError, setActionError] = useState<string | null>(null);
  const [matchOpen, setMatchOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // A different result opened in the same panel starts on that result.
  useEffect(() => { setViewingId(id); }, [id]);
  useEffect(() => { setActionError(null); }, [viewingId]);

  // Keyed as the Lab Results list's detail is, so LabMatchDialog's refresh
  // after an assignment reaches this view too.
  const { data: detail, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['lab-result-detail', 'access', viewingId],
    queryFn: () => getAccessLabResultDetail(viewingId),
    enabled: viewingId > 0,
    staleTime: 5 * 60 * 1000,
  });

  function toggleSection(key: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function openFile(url: string, name: string) {
    setActionError(null);
    try {
      if (!(await saveFile(url, name))) setActionError(PDF_OPEN_FAILED);
    } catch {
      setActionError(PDF_OPEN_FAILED);
    }
  }

  function handlePrint(d: AccessLabResultDetail) {
    setActionError(printAccessLabReport(d) ? null : PRINT_BLOCKED);
  }

  // The server records every version of the accession, and refuses if any
  // version has a patient (LabResultDeletions).
  async function handleDelete(resultId: number) {
    try {
      await deleteUnmatchedLabResults([{ source: 'access', id: resultId }]);
      toast.success('Lab result deleted', 'It has been removed from Lab Results.');
      void queryClient.invalidateQueries({ queryKey: ['lab-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
      onDeleted?.();
    } catch (error) {
      toast.error(
        'Lab result not deleted',
        getErrorMessage(error, 'This lab result could not be deleted. Refresh the list and try again.'),
      );
    }
  }

  const canDelete = detail != null && canDeleteLabResult(detail, user?.userLevel);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Status header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-6 py-3 flex-shrink-0" data-testid="access-result-header">
        <h2 className="text-base font-semibold text-slate-900">
          Accession #{detail?.orderNumber ?? viewingId}
          {detail?.patientName ? ` — ${detail.patientName}` : ''}
        </h2>
        {detail && (
          <>
            <AccessResultStatusBadge status={detail.status} label={detail.statusLabel} size="md" />
            {detail.isCurrentVersion && <Badge variant="neutral" size="md">Current</Badge>}
          </>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {detail && detail.versions.length > 1 && (
            <select
              aria-label="Version"
              value={String(viewingId)}
              onChange={(e) => setViewingId(Number(e.target.value))}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {detail.versions.map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {accessVersionLabel(v)}{v.isCurrent ? ' (Current)' : ''}
                </option>
              ))}
            </select>
          )}
          <Button size="sm" variant="secondary" disabled={!detail || isFetching} onClick={() => { if (detail) handlePrint(detail); }}>
            <Printer className="h-3.5 w-3.5 mr-1" />
            Print
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center gap-2 py-12">
            <Spinner />
            <p className="text-sm text-slate-500">Loading result…</p>
          </div>
        ) : isError || !detail ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">{LOAD_FAILED}</p>
            <Button size="sm" variant="secondary" onClick={() => { void refetch(); }}>Retry</Button>
          </div>
        ) : (
          <AccessDetailContent
            detail={detail}
            expandedSections={expandedSections}
            onToggle={toggleSection}
            onOpenFile={(url, name) => { void openFile(url, name); }}
            onViewCurrent={() => { if (detail.currentVersionId) setViewingId(detail.currentVersionId); }}
            onMatch={() => setMatchOpen(true)}
            onDelete={canDelete ? () => setDeleteOpen(true) : undefined}
            actionError={actionError}
          />
        )}
      </div>

      {matchOpen && detail && (
        <LabMatchDialog
          isOpen={matchOpen}
          onClose={() => setMatchOpen(false)}
          source="access"
          labResultId={detail.id}
          resultPatientName={detail.patientName}
          onAssociated={() => { onAssigned?.(); }}
        />
      )}

      {detail && (
        <ConfirmDialog
          isOpen={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={() => handleDelete(detail.id)}
          title="Delete this lab result?"
          message="It will be removed from Lab Results, along with any earlier versions of it. Use this only for results that do not belong to any patient."
          confirmLabel="Delete"
          variant="danger"
        />
      )}
    </div>
  );
}

interface AccessDetailContentProps {
  detail: AccessLabResultDetail;
  expandedSections: Set<string>;
  onToggle: (key: string) => void;
  onOpenFile: (url: string, name: string) => void;
  onViewCurrent: () => void;
  onMatch: () => void;
  /** Set only when this user may delete this held result. */
  onDelete?: () => void;
  actionError: string | null;
}

function AccessDetailContent({ detail, expandedSections, onToggle, onOpenFile, onViewCurrent, onMatch, onDelete, actionError }: AccessDetailContentProps) {
  const summary = detail.summary;
  const pdfUrl = summary.pdfReportUrl;
  const pdfFile = detail.files.find((f) => f.type.toUpperCase() === 'PDF');
  const otherFiles = detail.files.filter((f) => f.type.toUpperCase() !== 'PDF' && f.url);
  const pdfUnavailable = !pdfUrl && !!pdfFile;
  const sections = sectionsWithValues(detail);
  const hasValues = sections.length > 0;

  return (
    <>
      {!detail.isCurrentVersion && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span>This is an earlier version.</span>
          {detail.currentVersionId ? (
            <button type="button" onClick={onViewCurrent} className="font-medium text-blue-700 underline hover:text-blue-800">
              View the current version
            </button>
          ) : null}
        </div>
      )}

      {detail.patientId === 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">This result isn't linked to a patient.</p>
              <p className="text-xs text-amber-700">Match it to a patient to file it in their chart.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onDelete && (
              <Button variant="secondary" onClick={onDelete}>
                <Trash2 className="mr-1.5 h-4 w-4" />
                Delete
              </Button>
            )}
            <Button variant="secondary" onClick={onMatch}>
              <UserPlus className="mr-1.5 h-4 w-4" />
              Match to Patient
            </Button>
          </div>
        </div>
      )}

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</div>
      )}

      {/* Documents: the lab's report for this version, and any other files. */}
      {(pdfUrl || pdfUnavailable || otherFiles.length > 0) && (
        <div>
          <SectionHeader title="Documents" expanded={expandedSections.has('documents')} onToggle={() => onToggle('documents')} />
          {expandedSections.has('documents') && (
            <div className="space-y-1 ml-6">
              {pdfUrl && (
                <FileButton label={pdfFile?.label || 'Lab report'} onClick={() => onOpenFile(pdfUrl, pdfFile?.name || 'access_lab_report.pdf')} />
              )}
              {pdfUnavailable && <p className="text-sm text-slate-600">{PDF_UNAVAILABLE}</p>}
              {otherFiles.map((file, i) => (
                <FileButton key={i} label={file.label || file.name} onClick={() => onOpenFile(file.url as string, file.name || 'lab_file')} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Test results */}
      <div>
        <SectionHeader title="Tests" expanded={expandedSections.has('tests')} onToggle={() => onToggle('tests')} />
        {expandedSections.has('tests') && (
          <div className="space-y-4 ml-2">
            {!hasValues ? (
              <p className="text-sm text-slate-500 italic">{pdfUrl ? NO_VALUES_PDF : NO_VALUES}</p>
            ) : (
              sections.map((section, si) => (
                <div key={si} className="rounded-lg border border-slate-200 overflow-x-auto">
                  <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-emerald-900">{section.title}</span>
                    {section.subtitle && <span className="text-xs text-emerald-600">{section.subtitle}</span>}
                  </div>
                  <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2">Test</th>
                        <th className="px-4 py-2">Result</th>
                        <th className="px-4 py-2">Ref. Range</th>
                        <th className="px-4 py-2">Unit</th>
                        <th className="px-4 py-2">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {section.observations.map((obs, oi) => {
                        const flag = (obs.flag ?? '').toUpperCase();
                        const flagText = accessFlagText(obs.flag, obs.abnormal);
                        const flagStyle = flagText ? (FLAG_STYLES[flag] ?? FLAG_STYLES.A) : FLAG_STYLES.N;
                        return (
                          <tr key={oi} style={{ backgroundColor: flagStyle.bg, color: flagStyle.text }}>
                            <td className="px-4 py-2">
                              <span className="font-medium">{obs.label || obs.code}</span>
                              {obs.notes.length > 0 && (
                                <ul className="mt-1 list-disc pl-4 text-xs font-normal opacity-80">
                                  {obs.notes.map((n, ni) => <li key={ni}>{n}</li>)}
                                </ul>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span className="font-semibold">{obs.value}</span>
                              {obs.referenceRange && obs.value && (
                                <VisualRangeBar value={obs.value} referenceRange={obs.referenceRange} abnormalFlag={flag} />
                              )}
                            </td>
                            <td className="px-4 py-2 text-slate-500">{obs.referenceRange ?? '—'}</td>
                            <td className="px-4 py-2 text-slate-500">{obs.unit ?? '—'}</td>
                            <td className="px-4 py-2">
                              {flagText ? <span className="text-xs font-semibold">{flagText}</span> : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))
            )}
            {detail.notes.length > 0 && (
              <div className="rounded-lg border border-slate-200 px-4 py-3">
                <h4 className="text-sm font-semibold text-slate-700">Notes</h4>
                <ul className="mt-1 list-disc pl-4 text-sm text-slate-700">
                  {detail.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div>
        <SectionHeader title="Details" expanded={expandedSections.has('details')} onToggle={() => onToggle('details')} />
        {expandedSections.has('details') && (
          <div className="ml-6 rounded-lg border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-semibold text-slate-700">Patient</h4>
              <p><span className="text-slate-500">Name:</span> {detail.patientName || '—'}</p>
              <p><span className="text-slate-500">DOB:</span> {formatAccessBirthDate(detail.birthDate)}</p>
              <p><span className="text-slate-500">ID:</span> {detail.patientDisplayId ?? '—'}</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-slate-700">Result</h4>
              <p><span className="text-slate-500">Accession #:</span> {detail.orderNumber ?? '—'}</p>
              <p><span className="text-slate-500">Collected:</span> {formatAccessDateTime(summary.collectedAt)}</p>
              <p><span className="text-slate-500">Received:</span> {formatAccessDateTime(accessReceivedAt(detail))}</p>
              {summary.providerName ? <p><span className="text-slate-500">Provider:</span> {summary.providerName}{summary.providerNpi ? ` (${summary.providerNpi})` : ''}</p> : null}
              {summary.location ? <p><span className="text-slate-500">Location:</span> {summary.location}</p> : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** A collapsible section's header: the chevron and the section's title. */
function SectionHeader({ title, expanded, onToggle }: { title: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} className="flex items-center gap-2 w-full text-left py-2">
      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{title}</h3>
    </button>
  );
}

function FileButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 w-full text-left rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
    >
      <FileText className="h-4 w-4 text-red-500 shrink-0" />
      <span className="text-slate-800">{label}</span>
      <Download className="h-3.5 w-3.5 text-slate-400 ml-auto" />
    </button>
  );
}

interface AccessLabDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** labAccessResultID. */
  labResultId: number;
}

export function AccessLabDetailModal({ open, onClose, labResultId }: AccessLabDetailModalProps) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-end border-b border-slate-200 px-3 py-2 flex-shrink-0">
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          {/* Assigned or deleted from the unmatched list: the result has left that list, so close. */}
          <AccessLabDetailBody id={labResultId} onAssigned={onClose} onDeleted={onClose} />
        </div>
      </div>
    </div>
  );
}
